import apiClient from '@/clients/apiClient';

// The API uses PascalCase enum names (Individual/Company, Active/Deactivated);
// the UI uses lowercase (individual/company, active/inactive). These helpers bridge the two.
const toApiEntityType = (t) => (String(t).toLowerCase() === 'company' ? 'Company' : 'Individual');
const toUiEntityType = (t) => (String(t).toLowerCase() === 'company' ? 'company' : 'individual');
const toUiStatus = (s) => (String(s).toLowerCase() === 'active' ? 'active' : 'inactive');
const toIdentifierType = (t) => (String(t).toLowerCase() === 'company' ? 'RC' : 'BVN');

export const mapBlacklistEntry = (e = {}) => ({
  id: e.id,
  name: e.name,
  entityType: toUiEntityType(e.entityType),
  identifier: e.identifier || '',
  identifierType: e.identifierType || toIdentifierType(e.entityType),
  source: e.source || '',
  notes: e.notes || '',
  dateAdded: e.dateAdded,
  status: toUiStatus(e.status),
  addedBy: { name: e.addedBy || '—', role: 'Compliance' },
  linkGroupId: e.linkGroupId || null,
  pendingMatchCount: e.pendingMatchCount || 0,
  linkedEntries: (e.linkedEntries || []).map((l) => ({
    id: l.id,
    name: l.name,
    entityType: toUiEntityType(l.entityType),
    identifier: l.identifier || '',
    identifierType: toIdentifierType(l.entityType),
  })),
});

const buildEntryPayload = (form = {}) => ({
  entityType: toApiEntityType(form.entityType),
  name: (form.name || '').trim(),
  identifier: (form.identifier || '').trim() || null,
  source: form.source,
  notes: (form.notes || '').trim() || null,
  aliases: form.aliases && form.aliases.length ? form.aliases : null,
  relatedEntryId: form.relatedEntryId || null,
});

export const getBlacklistEntries = (params = {}) => {
  const { page = 1, pageSize = 200, search = '', entityType = '', source = '', status = '' } = params;
  const q = new URLSearchParams();
  q.set('Page', page);
  q.set('PageSize', pageSize);
  if (search) q.set('Search', search);
  if (entityType) q.set('EntityType', toApiEntityType(entityType));
  if (source) q.set('Source', source);
  if (status) q.set('Status', status === 'inactive' ? 'Deactivated' : 'Active');
  return apiClient.get(`blacklist?${q.toString()}`);
};

export const getBlacklistEntry = (id) => apiClient.get(`blacklist/${id}`);

export const createBlacklistEntry = (form) => apiClient.post('blacklist', buildEntryPayload(form));

export const updateBlacklistEntry = (id, form) =>
  apiClient.put(`blacklist/${id}`, buildEntryPayload(form));

export const bulkCreateBlacklist = (source, entries = []) =>
  apiClient.post('blacklist/bulk', {
    entries: entries.map((e) => buildEntryPayload({ ...e, source: e.source || source })),
  });

export const deactivateBlacklistEntry = (id, reason) =>
  apiClient.post(`blacklist/${id}/deactivate`, { reason });

export const reactivateBlacklistEntry = (id) => apiClient.post(`blacklist/${id}/reactivate`);

// Immediately screen a single entry against all active customers/businesses.
export const screenBlacklistEntry = (id) => apiClient.post(`blacklist/${id}/screen`);

// All screening matches for one entry (inline review panel in the entry drawer).
export const getEntryMatches = (id) => apiClient.get(`blacklist/${id}/matches`);

// Ownership-related accounts for a subject (a customer's businesses, or a business's owner + businesses).
export const getRelatedAccounts = (subjectType, subjectId) =>
  apiClient.get(`blacklist/related?subjectType=${subjectType}&subjectId=${subjectId}`);

// Blacklist a specific customer/business by association.
export const markSubjectBlacklisted = (subjectType, subjectId, reason) =>
  apiClient.post('blacklist/subjects/blacklist', { subjectType, subjectId, reason });

// Compliance review queue
export const getBlacklistMatches = (params = {}) => {
  const { page = 1, pageSize = 50, status = '', screen = '', subjectType = '' } = params;
  const q = new URLSearchParams();
  q.set('Page', page);
  q.set('PageSize', pageSize);
  if (status) q.set('Status', status);
  if (screen) q.set('Screen', screen);
  if (subjectType) q.set('SubjectType', subjectType);
  return apiClient.get(`blacklist/matches?${q.toString()}`);
};

export const confirmBlacklistMatch = (matchId, reason) =>
  apiClient.post(`blacklist/matches/${matchId}/confirm`, { reason });

export const dismissBlacklistMatch = (matchId, reason) =>
  apiClient.post(`blacklist/matches/${matchId}/dismiss`, { reason });
